import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Button from '../TailwindComponents/Button';
import Input from '../TailwindComponents/Input';
import Textarea from '../TailwindComponents/Textarea';
import Dropdown, { DropdownOption } from '../TailwindComponents/Dropdown';
import FileUploader from '../TailwindComponents/FileUploader';
import DateTimePicker from '../TailwindComponents/DatePicker';
import CollaboratorSelector, { Collaborator } from '../CollaboratorSelector';
import FieldShell, { FIELD_HELP_CLASS } from '../TailwindComponents/FieldShell';
import FormShell from './FormShell';
import GlassCard from '../brand/GlassCard';
import { PiTrashLight } from 'react-icons/pi';
import { FormSchema, FormValidator } from '../../../utils/validation/form-schema';
import { MAX_UPLOAD_BYTES } from '../../../utils/uploadLimits';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { showModal, hideModal } from '../../../store/actions/modalActions';
import { useAppDispatch } from '../../../store';
import BrandLoader from '../brand/BrandLoader';
import AIProcessing from '../brand/AIProcessing';
import 'react-datepicker/dist/react-datepicker.css';

export type FieldType = 'input' | 'textarea' | 'dropdown' | 'fileUploader' | 'datePicker' | 'collaboratorSelector' | 'custom';

export interface BaseFormFieldConfig {
  name: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  required?: boolean;
  helpText?: string;
  description?: string;
  disabled?: boolean;
  gridSize?: 'full' | 'half' | 'third';
  inputType?: string;
  options?: DropdownOption[];
  rows?: number;
  allowedExtensions?: string[];
  maxFileSize?: number;
  maxFiles?: number;
  dateFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  customComponent?: React.ComponentType<any>;
  customProps?: Record<string, any>;
  hidden?: boolean;
}

export interface BaseFormSectionConfig {
  title?: string;
  description?: string;
  fields: BaseFormFieldConfig[];
  gridCols?: number;
  /** Optional data-tour-id for onboarding tour targeting. */
  dataTourId?: string;
}

export interface BaseFormProps {
  title: string;
  description?: string;
  sections: BaseFormSectionConfig[] | BaseFormFieldConfig[];
  /**
   * Rendered above the first section, inside the content column.
   *
   * For what the form must say before any field is read - the template
   * libraries use it for "you are creating a STRUCTURE template, and here is
   * how to create a different kind instead". Not a section: it holds no
   * fields and takes no card of its own.
   */
  leadIn?: React.ReactNode;
  schema: FormSchema;
  initialValues?: Record<string, any>;
  onSubmit: (data: Record<string, any>) => Promise<void> | void;
  onSecondarySubmit?: (data: Record<string, any>) => Promise<void> | void;
  onCancel?: () => void;
  submitLabel?: string;
  secondarySubmitLabel?: string;
  /** Independently disable the secondary submit button (e.g. when a prerequisite is missing). */
  secondarySubmitDisabled?: boolean;
  /** Tooltip shown when the secondary submit button is disabled via secondarySubmitDisabled. */
  secondarySubmitTooltip?: string;
  /** Independently disable the primary submit button (e.g. when a feature is locked). */
  submitDisabled?: boolean;
  /** Tooltip shown when the primary submit button is disabled via submitDisabled. */
  submitTooltip?: string;
  cancelLabel?: string;
  /** Optional back button (e.g. for wizard navigation). Renders on the left side of the footer. */
  onBack?: () => void;
  backLabel?: string;
  showConfirmationModal?: boolean;
  isSubmitting?: boolean;
  hideActions?: boolean;
  hideLoadingOverlay?: boolean;
  hideHeader?: boolean;
  className?: string;
  maxWidth?: string;
  loadingText?: string;
  progressSteps?: Array<{ name: string; status?: 'pending' | 'processing' | 'completed' | 'failed'; message?: string }>;
  isConnecting?: boolean;
  onValuesChange?: (values: Record<string, any>, changedField: string) => void;
}

const isSection = (item: any): item is BaseFormSectionConfig => {
  return 'fields' in item && Array.isArray(item.fields);
};

/**
 * Tailwind's JIT scans source text for complete class names, so the old
 * `grid-cols-${section.gridCols || 1}` never made it into the stylesheet - the
 * layout was being held up entirely by an inline `gridTemplateColumns` that
 * shadowed it. A static map is what makes the class real, and it lets the
 * columns stack on narrow screens, which the fixed inline `repeat(3, 1fr)`
 * could not do.
 */
const GRID_COLS_CLASS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 md:grid-cols-2',
  3: 'grid-cols-1 md:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
};

const BaseForm: React.FC<BaseFormProps> = ({
  title,
  description,
  sections,
  leadIn,
  schema,
  initialValues = {},
  onSubmit,
  onSecondarySubmit,
  onCancel,
  submitLabel,
  secondarySubmitLabel,
  secondarySubmitDisabled,
  secondarySubmitTooltip,
  submitDisabled,
  submitTooltip,
  cancelLabel,
  onBack,
  backLabel,
  showConfirmationModal = true,
  isSubmitting = false,
  hideActions = false,
  hideLoadingOverlay = false,
  hideHeader = false,
  className,
  maxWidth = 'max-w-3xl',
  loadingText,
  progressSteps,
  isConnecting = false,
  onValuesChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const modalState = useSelector((state: RootState) => state.modal);
  const [formValues, setFormValues] = useState<Record<string, any>>(initialValues);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [isDirty, setIsDirty] = useState(false);

  // Track which fields the user has explicitly modified - never reset these
  const dirtyFieldsRef = useRef<Set<string>>(new Set());

  // Sync formValues when initialValues change externally (e.g. collaborators from parent folder, org-wide toggle)
  // Only fires when initialValues reference changes - NOT when isDirty changes.
  // Fields the user has explicitly modified are never overwritten.
  useEffect(() => {
    setFormValues((prev) => {
      const updated = { ...prev };
      let changed = false;
      for (const key of Object.keys(initialValues)) {
        // Never reset fields the user has explicitly changed
        if (dirtyFieldsRef.current.has(key)) continue;

        if (JSON.stringify(prev[key]) !== JSON.stringify(initialValues[key])) {
          updated[key] = initialValues[key];
          changed = true;
        }
      }
      return changed ? updated : prev;
    });
  }, [initialValues]);
  const [localIsSubmitting, setLocalIsSubmitting] = useState(false);

  const formattedSections: BaseFormSectionConfig[] = useMemo(() => {
    if (sections.length === 0) return [];
    if (isSection(sections[0])) {
      return sections as BaseFormSectionConfig[];
    }
    return [{ fields: sections as BaseFormFieldConfig[] }];
  }, [sections]);

  const validateForm = useCallback(() => {
    const validator = new FormValidator(schema);
    const validation = validator.validate(formValues, false);
    setErrors(validation.errors);
    return validation.isValid;
  }, [formValues, schema]);

  const isFormValid = useMemo(() => {
    const validator = new FormValidator(schema);
    const validation = validator.validate(formValues, false);
    return validation.isValid;
  }, [formValues, schema]);

  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    dirtyFieldsRef.current.add(fieldName);
    setFormValues((prev) => {
      const updated = { ...prev, [fieldName]: value };
      onValuesChange?.(updated, fieldName);
      return updated;
    });
    setIsDirty(true);

    const field = schema.getField(fieldName);
    if (field) {
      const result = field.validate(value);
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (result.isValid) {
          delete newErrors[fieldName];
        } else {
          newErrors[fieldName] = [result.error || t('Invalid value')];
        }
        return newErrors;
      });
    }
  }, [schema, onValuesChange]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setLocalIsSubmitting(true);
    try {
      await onSubmit(formValues);
    } finally {
      setLocalIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isDirty && showConfirmationModal) {
      dispatch(
        showModal({
          showModal: true,
          type: 'confirmation',
          title: t('Discard Changes'),
          message: t('You have unsaved changes. Are you sure you want to discard them?'),
          subText: t('Anything you have typed in this form will be lost.'),
          icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
          variant: 'danger',
          confirmLabel: t('Discard'),
          onConfirm: () => {
            dispatch(hideModal());
            onCancel?.();
          },
          onClose: () => {
            dispatch(hideModal());
          },
        })
      );
    } else {
      onCancel?.();
    }
  };

  const renderField = (field: BaseFormFieldConfig) => {
    if (field.hidden) return null;

    // `half` and `third` used to be the same three no-op classes, so a field
    // asking for a third was indistinguishable from one asking for a half. They
    // are spans against the section's column count now, clamped by the grid
    // itself when the section is narrower than the span.
    const gridClass = {
      full: 'col-span-full',
      half: 'col-span-1',
      third: 'col-span-1',
    }[field.gridSize || 'full'];

    const fieldError = errors[field.name]?.[0];
    const isDisabled = field.disabled || localIsSubmitting || isSubmitting;

    return (
      <div key={field.name} className={gridClass} data-tour-id={`field-${field.name}`}>
        {field.type === 'input' && (
          <Input
            id={field.name}
            name={field.name}
            label={field.label}
            placeholder={field.placeholder}
            type={field.inputType || 'text'}
            value={formValues[field.name] ?? ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            error={fieldError ? true : false}
            helperText={fieldError}
            disabled={isDisabled}
            required={field.required}
            description={field.description}
          />
        )}

        {field.type === 'textarea' && (
          <Textarea
            id={field.name}
            name={field.name}
            label={field.label}
            placeholder={field.placeholder}
            value={formValues[field.name] ?? ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            error={fieldError}
            disabled={isDisabled}
            required={field.required}
            description={field.description}
            rows={field.rows || 4}
          />
        )}

        {field.type === 'dropdown' && (
          <Dropdown
            label={field.label}
            options={field.options || []}
            value={formValues[field.name] ?? (field.customProps?.multiple ? [] : '')}
            onChange={(value) => handleFieldChange(field.name, value)}
            placeholder={field.placeholder}
            useApi={false}
            required={field.required}
            {...field.customProps}
          />
        )}

        {/* The uploader has no label chrome of its own, so the shell draws it —
            the same shell Input/Textarea/Dropdown/DatePicker route through, so
            an uploader's label sits at the same size, weight and rhythm as the
            field above it instead of its own `mb-2` + `ms-1` asterisk. */}
        {field.type === 'fileUploader' && (
          <FieldShell
            label={field.label}
            required={field.required}
            description={field.description}
            error={fieldError}
            helpText={field.helpText}
            translate={false}
            className="flex flex-col"
          >
            <FileUploader
              files={formValues[field.name] || []}
              onFilesChange={(files) => handleFieldChange(field.name, files)}
              allowedExtensions={field.allowedExtensions}
              maxSizeBytes={field.maxFileSize || MAX_UPLOAD_BYTES}
              maxFiles={field.maxFiles}
            />
          </FieldShell>
        )}

        {field.type === 'datePicker' && (
          <DateTimePicker
            id={field.name}
            label={field.label}
            selected={formValues[field.name] ? new Date(formValues[field.name]) : null}
            onChange={(date) => handleFieldChange(field.name, date?.toISOString() || '')}
            placeholder={field.placeholder}
            minDate={field.minDate}
            maxDate={field.maxDate}
            disabled={isDisabled}
            dateFormat={field.dateFormat || 'MMM d, yyyy'}
            showTimeSelect={true}
            required={field.required}
            error={fieldError ? true : false}
            helperText={fieldError}
            fullWidth={field.customProps?.fullWidth || false}
          />
        )}

        {field.type === 'collaboratorSelector' && (
          <CollaboratorSelector
            collaborators={formValues[field.name] || []}
            onCollaboratorsChange={(collaborators: Collaborator[]) => handleFieldChange(field.name, collaborators)}
            // customProps spreads FIRST here, unlike the `custom` branch above.
            // `title` is this control's label slot, and five forms pass a
            // `customProps.title` alongside a declared `label` - spreading last
            // let that stale value overwrite the field's own label with no
            // warning. The declared label is the field's identity, so it wins.
            {...field.customProps}
            title={field.label}
            description={field.description ?? field.customProps?.description}
          />
        )}

        {field.type === 'custom' && field.customComponent && (
          <field.customComponent
            id={field.name}
            name={field.name}
            label={field.label}
            value={formValues[field.name]}
            onChange={(value: any) => handleFieldChange(field.name, value)}
            disabled={isDisabled}
            required={field.required}
            // Custom fields validate through the same schema as every other
            // field but never received the verdict, so 59 call sites could go
            // invalid without being able to say so. customProps still spreads
            // last, so a component with its own error/helpText contract wins.
            error={fieldError}
            helpText={field.helpText}
            description={field.description}
            {...field.customProps}
          />
        )}

        {/* helpText was accepted by the config and then dropped on the floor.
            Custom fields get it as a prop above and fileUploader gets it from
            its own FieldShell, so this is for the rest - same class and same
            step as the shell's own help row. */}
        {field.type !== 'custom' && field.type !== 'fileUploader' && field.helpText && !fieldError && (
          <p className={clsx('mt-1.5', FIELD_HELP_CLASS)}>{field.helpText}</p>
        )}
      </div>
    );
  };

  /* Submission Loading Overlay */
  const overlay =
    (localIsSubmitting || isSubmitting) && !hideLoadingOverlay ? (
        <div className="absolute inset-0 bg-bg/80 backdrop-blur-[2px] flex items-center justify-center z-50 rounded-lg">
          {progressSteps && progressSteps.length > 0 ? (
            <AIProcessing
              variant="inline"
              title={loadingText || t('Processing...')}
              className="!bg-transparent max-h-96 overflow-y-auto"
              steps={progressSteps.map((step, idx) => ({
                key: String(idx),
                label: step.name,
                detail: step.message,
                status:
                  step.status === 'completed'
                    ? 'done'
                    : step.status === 'processing'
                    ? 'active'
                    : step.status === 'failed'
                    ? 'error'
                    : 'pending',
              }))}
            />
          ) : (
            <BrandLoader size="lg" />
          )}
        </div>
    ) : null;

  return (
    <FormShell
      as="form"
      onSubmit={handleSubmit}
      className={clsx('h-full min-h-0', className)}
      overlay={overlay}
      title={hideHeader ? undefined : title}
      description={hideHeader ? undefined : description}
      hideHeader={hideHeader}
      maxWidth={maxWidth}
      footer={
        <>
          {onBack && !hideActions ? (
            <Button
              variant="neutral"
              size="small"
              onClick={onBack}
              disabled={localIsSubmitting || isSubmitting}
            >
              {backLabel || t('Back')}
            </Button>
          ) : (
            /* Always `justify-between` with a spacer standing in for an absent
               back button. Switching to `justify-end` on the steps that have no
               back button made the primary button jump sideways between wizard
               steps, which is the one thing a step footer must not do. With a
               zero-width spacer the right-hand group lands exactly where
               `justify-end` put it, so single-action forms are unchanged. */
            <div aria-hidden="true" />
          )}
          <div className="flex gap-2">
            {onCancel && !hideActions && (
              <Button
                variant="neutral"
                size="small"
                onClick={handleCancel}
                disabled={localIsSubmitting || isSubmitting}
              >
                {cancelLabel || t('Cancel')}
              </Button>
            )}
            {!hideActions && onSecondarySubmit && (
              <Button
                variant="neutral"
                size="small"
                type="button"
                disabled={!isFormValid || localIsSubmitting || isSubmitting || secondarySubmitDisabled}
                isLoading={localIsSubmitting || isSubmitting}
                tooltip={secondarySubmitDisabled ? secondarySubmitTooltip : undefined}
                onClick={async (e: React.MouseEvent) => {
                  e.preventDefault();
                  if (isFormValid && !secondarySubmitDisabled && onSecondarySubmit) {
                    await onSecondarySubmit(formValues);
                  }
                }}
              >
                {secondarySubmitLabel || t('Submit & Process')}
              </Button>
            )}
            {!hideActions && (
              <Button
                variant="primary"
                size="small"
                type="submit"
                disabled={!isFormValid || localIsSubmitting || isSubmitting || submitDisabled}
                isLoading={localIsSubmitting || isSubmitting}
                tooltip={submitDisabled ? submitTooltip : undefined}
              >
                {submitLabel || t('Submit')}
              </Button>
            )}
          </div>
        </>
      }
    >
      {/*
        Above the first section, inside the content column.

        For the thing the form needs to say before any field is read - which
        kind of record this is, and how to change your mind about it. It is not
        a section: it has no fields, and wrapping it in a `GlassCard` would put
        a card about the form inside the form's own body, reading as the first
        question rather than as the answer to what you are filling in.
      */}
      {leadIn}

      {formattedSections.map((section, sectionIdx) => {
        const grid = (
          <div
            className={clsx(
              'grid gap-4',
              GRID_COLS_CLASS[section.gridCols ?? 1] || GRID_COLS_CLASS[1]
            )}
          >
            {section.fields.map((field) => renderField(field))}
          </div>
        );

        // A titled section is a section - give it the app's section
        // container. Untitled ones are just the form's own body and would
        // read as a card floating on a card.
        return section.title ? (
          <GlassCard
            key={sectionIdx}
            title={section.title}
            subtitle={section.description}
            data-tour-id={section.dataTourId}
          >
            {grid}
          </GlassCard>
        ) : (
          <div key={sectionIdx} data-tour-id={section.dataTourId}>
            {grid}
          </div>
        );
      })}
    </FormShell>
  );
};

export default BaseForm;
